/**
 * Required-certificate uploads for the supplier quote form.
 *
 * Each required certificate gets a real file picker wired to
 * `POST /rfq/certificate-submissions/` (multipart). Existing submissions on
 * the response are listed with their verification status so the supplier
 * can see what is already on file.
 */
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Shield, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI } from '../../lib/api';
import type { RequiredCertificate, SupplierCertificateSubmission } from '../../lib/api';
import FileDropZone from '../common/FileDropZone';

interface SupplierResponseCertificatesProps {
  responseId: number;
  certificates: RequiredCertificate[];
  submissions: SupplierCertificateSubmission[];
  disabled: boolean;
  formatDate: (date: string) => string;
  onUploaded: () => void;
}

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.doc,.docx';
const MAX_BYTES = 10 * 1024 * 1024;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function SupplierResponseCertificates({
  responseId,
  certificates,
  submissions,
  disabled,
  formatDate,
  onUploaded,
}: SupplierResponseCertificatesProps) {
  const [uploadingFor, setUploadingFor] = useState<number | null>(null);
  const [issueDates, setIssueDates] = useState<Record<number, string>>({});
  const [expiryDates, setExpiryDates] = useState<Record<number, string>>({});

  if (!certificates.length) return null;

  const submissionsFor = (certId: number) =>
    submissions.filter((s) => s.required_certificate === certId);

  // Customer review 11 Sep (drag and drop everywhere): type and 10 MB cap are
  // enforced by each certificate's FileDropZone, for drops and picks alike.
  const handleFile = async (cert: RequiredCertificate, file: File | null) => {
    if (!file) return;
    const form = new FormData();
    form.append('response', String(responseId));
    form.append('required_certificate', String(cert.id));
    form.append('certificate_file', file);
    form.append('issue_date', issueDates[cert.id] || todayIso());
    if (expiryDates[cert.id]) form.append('expiry_date', expiryDates[cert.id]);
    try {
      setUploadingFor(cert.id);
      await rfqAPI.uploadCertificateSubmission(form);
      toast.success(`${cert.certificate_name} uploaded`);
      onUploaded();
    } catch (err: unknown) {
      console.error('Error uploading certificate:', err);
      toast.error('Failed to upload certificate');
    } finally {
      setUploadingFor(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Required Certificates
        </CardTitle>
        <CardDescription>Upload required certificates before submission</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {certificates.map((cert) => {
            const existing = submissionsFor(cert.id);
            const busy = uploadingFor === cert.id;
            return (
              <div key={cert.id} className="p-3 border rounded-lg space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{cert.certificate_name}</span>
                      <Badge variant={cert.is_mandatory ? 'default' : 'secondary'} className="text-xs">
                        {cert.is_mandatory ? 'Required' : 'Optional'}
                      </Badge>
                    </div>
                    {cert.description && (
                      <p className="text-xs text-muted-foreground">{cert.description}</p>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <div>
                      <Label className="text-xs" htmlFor={`cert-issue-${cert.id}`}>Issue date</Label>
                      <Input
                        id={`cert-issue-${cert.id}`}
                        type="date"
                        className="h-8 text-xs"
                        value={issueDates[cert.id] ?? todayIso()}
                        onChange={(e) => setIssueDates((prev) => ({ ...prev, [cert.id]: e.target.value }))}
                        disabled={disabled || busy}
                      />
                    </div>
                    <div>
                      <Label className="text-xs" htmlFor={`cert-expiry-${cert.id}`}>Expiry date</Label>
                      <Input
                        id={`cert-expiry-${cert.id}`}
                        type="date"
                        className="h-8 text-xs"
                        value={expiryDates[cert.id] ?? ''}
                        onChange={(e) => setExpiryDates((prev) => ({ ...prev, [cert.id]: e.target.value }))}
                        disabled={disabled || busy}
                      />
                    </div>
                  </div>
                </div>
                <FileDropZone
                  size="compact"
                  accept={ACCEPT}
                  maxSize={MAX_BYTES}
                  disabled={disabled}
                  busy={busy}
                  hint="PDF, PNG, JPG, DOC or DOCX (max 10MB)"
                  aria-label={`Upload ${cert.certificate_name}`}
                  inputTestId={`cert-file-${cert.id}`}
                  onFiles={(files) => void handleFile(cert, files[0] ?? null)}
                />
                {existing.length > 0 && (
                  <ul className="space-y-1">
                    {existing.map((s) => (
                      <li key={s.id} className="text-xs text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <FileCheck className="h-3.5 w-3.5 text-green-600" />
                          <span className="text-foreground">{s.file_name}</span>
                          <span>uploaded {formatDate(s.uploaded_at)}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {s.verification_status_display || s.verification_status}
                          </Badge>
                        </div>
                        {/* The buyer's Reject dialog promises the reason is
                            "shown to the supplier" — this is where. */}
                        {s.verification_status === 'rejected' && s.rejection_reason && (
                          <p className="ml-5 text-[11px] text-destructive">Reason: {s.rejection_reason}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
